import { FC, useState } from 'react';
import {
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import EmailTemplateConfigurationModal, {
  EmailTemplateSection,
} from 'dashboard409a/components/EmailTemplateConfigurationModal';
import { contactSelectKey } from 'dashboard409a/utilities';
import {
  COMPANY_NAME_PLACEHOLDER,
  CONTACT_FIRST_NAME_PLACEHOLDER,
} from '../OnboardingEmailModal.templates';
import { useOnboardingEmailModalContext } from '../OnboardingEmailModalContext';

const OnboardingEmailOutputPanel: FC = () => {
  const {
    contacts,
    isLoadingContacts,
    selectedContactKey,
    setSelectedContactKey,
    templates,
    presets,
    selectedTemplateId,
    setSelectedTemplateId,
    emailSubject,
    introduction,
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
    draftEmail,
    draftSubject,
    setDraftSubject,
    draftBody,
    setDraftBody,
    flushDraftEmail,
    portalLink,
    handleCopyEmail,
    handleCopyPortalLink,
  } = useOnboardingEmailModalContext();

  const [emailTemplateConfigOpen, setEmailTemplateConfigOpen] = useState(false);

  return (
    <Stack spacing={2}>
      <FormControl size="small" fullWidth>
        {/* displayEmpty renders the placeholder item, so keep the label shrunk to avoid overlap. */}
        <InputLabel id="onboarding-email-contact-label" shrink>
          Onboarding Contact
        </InputLabel>
        <Select
          labelId="onboarding-email-contact-label"
          id="onboarding-email-contact"
          label="Onboarding Contact"
          notched
          value={selectedContactKey}
          onChange={event => setSelectedContactKey(event.target.value)}
          disabled={isLoadingContacts || contacts.length === 0}
          displayEmpty
          MenuProps={{
            // Nested inside Operations Modal; keep the menu in-tree so clicks register.
            disablePortal: true,
            PaperProps: { sx: { maxHeight: 320 } },
          }}
        >
          {contacts.length === 0 && (
            <MenuItem value="" disabled>
              {isLoadingContacts ? 'Loading contacts…' : 'No contacts found'}
            </MenuItem>
          )}
          {contacts.map((contact, index) => {
            const key = contactSelectKey(contact, index);
            return (
              <MenuItem key={key} value={key}>
                {contact.name}
                {contact.email ? ` (${contact.email})` : ''}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <EmailTemplateSection
        idPrefix="onboarding-email"
        templateSelectLabel="Document request template"
        templates={templates}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        emailSubject={emailSubject}
        onConfigure={() => setEmailTemplateConfigOpen(true)}
      />

      <EmailTemplateConfigurationModal
        open={emailTemplateConfigOpen}
        onClose={() => setEmailTemplateConfigOpen(false)}
        idPrefix="onboarding-email"
        title="Configure onboarding email template"
        presets={presets}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        introduction={introduction}
        setIntroduction={setIntroduction}
        introductionPlaceholders={[
          { label: COMPANY_NAME_PLACEHOLDER, token: COMPANY_NAME_PLACEHOLDER },
          { label: CONTACT_FIRST_NAME_PLACEHOLDER, token: CONTACT_FIRST_NAME_PLACEHOLDER },
        ]}
        subjectHelperText="Use {{COMPANY_NAME}} and {{CONTACT_FIRST_NAME}} as needed."
        introductionHelperText="Use {{COMPANY_NAME}} for the company and {{CONTACT_FIRST_NAME}} for the selected contact. The document list and portal link are added automatically."
        savePresetOpen={savePresetOpen}
        presetName={presetName}
        setPresetName={setPresetName}
        handleOpenSavePreset={handleOpenSavePreset}
        handleCloseSavePreset={handleCloseSavePreset}
        handleSavePreset={handleSavePreset}
        handleApplyPreset={handleApplyPreset}
        handleDeletePreset={handleDeletePreset}
        handleResetToDefault={handleResetToDefault}
      />

      <TextField
        label="Subject"
        value={draftSubject}
        onChange={event => setDraftSubject(event.target.value)}
        onBlur={flushDraftEmail}
        placeholder="Subject will appear here after generation…"
        fullWidth
        size="small"
        id="onboarding-email-subject"
      />
      <TextField
        label="Email"
        value={draftBody}
        onChange={event => setDraftBody(event.target.value)}
        onBlur={flushDraftEmail}
        placeholder="Email will appear here after generation…"
        multiline
        minRows={12}
        fullWidth
        id="onboarding-email-content"
      />
      <TextField
        label="Document request portal link"
        value={portalLink ?? ''}
        placeholder="Portal link will appear here after generation…"
        fullWidth
        size="small"
        InputProps={{ readOnly: true }}
        id="onboarding-email-portal-link"
      />
      <Divider />
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={handleCopyEmail}
          disabled={!draftEmail}
          id="copy-onboarding-email-button"
        >
          Copy Email
        </Button>
        <Button
          variant="outlined"
          onClick={handleCopyPortalLink}
          disabled={!portalLink}
          id="copy-onboarding-portal-link-button"
        >
          Copy Link
        </Button>
      </Stack>
    </Stack>
  );
};

export default OnboardingEmailOutputPanel;
